// MasterPitAddEdit.jsx
'use client';

import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Stack,
  TextField,
  CircularProgress,
  Button,
  FormControlLabel,
  Switch,
  InputAdornment,
  Typography,
  IconButton
} from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import MapIcon from '@mui/icons-material/Map';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import DrawPolygon from '../../../components/DrawPolygon';
import PickerField from '../../../components/PickerField';
import FormDialog from '../../../components/FormDialog';
import { useNotifier } from '../../../components/Notifier'; // Import useNotifier

import { createPool, updatePool, getPoolById } from '../../../stores/poolStore';
import { useCompanyDataList } from '../../../stores/companyStore';
import usePickerPagination from '../../../hooks/usePickerPagination';
import { useCountryList } from '../../../stores/countryStore';
import { useAreaList } from '../../../stores/areaStore';

import {
  featureToFeatureCollection,
  geojsonToWKT,
  polygonCoordsStringFromGeoJSON,
  computeCentroidFromGeoJSON,
  wktToGeoJSON
} from '../../../utils/geo';
import MasterAreaAddEdit from '../working-area/MasterAreaAddEdit';
import MasterCountryAddEdit from '../country/MasterCountryAddEdit';
import MasterCompanyAddEdit from '../company/MasterCompanyAddEdit';
import { formatUpperCase } from '../../../utils/formatTitleCase';
import { useLanguage } from '../../../utils/i18n';

const MasterPitAddEdit = forwardRef(
  (
    {
      open,
      id = null,
      onClose = null,
      onSuccess = null,
      onError = null,
      onSubmitStart = () => {},
      onSubmitEnd = () => {},
      passedWkt = null,
      disabledViewMap = false
    },
    ref
  ) => {
    const { t } = useLanguage();
    const notify = useNotifier(); // Inisialisasi useNotifier
    const [isEditingGeom, setIsEditingGeom] = useState(false);
    const [initialValues, setInitialValues] = useState({
      company: null,
      company_name: '',
      name: '',
      pool_code: '',
      country: null,
      country_name: '',
      pool_group: null,
      pool_group_name: '',
      area: null,
      area_name: '',
      address: '',
      //  pic_name: '',
      //  pic_phone: '',
      remark: '',
      status: true,
      min_speed: '',
      max_speed: '',

      geom: null,
      geom_wkb: null,
      coords: null,
      centroid_lat: null,
      centroid_lon: null,
      info: null,
      latitude: null,
      longitude: null
    });

    const [loading, setLoading] = useState(Boolean(id));
    const [mapOpen, setMapOpen] = useState(false);
    const formikRef = useRef();

    const companyPickerParams = usePickerPagination({
      fetchHook: useCompanyDataList,
      buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
      searchKey: 'name'
    });

    const [filterCompanyId, setFilterCompanyId] = useState('');

    const countryPickerParams = usePickerPagination({
      fetchHook: useCountryList,
      buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue, state.companyId],
      searchKey: 'name',
      companyId: filterCompanyId
    });

    const areaPickerParams = usePickerPagination({
      fetchHook: useAreaList,
      buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue, state.companyId],
      searchKey: 'name',
      companyId: filterCompanyId
    });

    const companyOptions = companyPickerParams.options;
    const countryOptions = countryPickerParams.options;
    const areaOptions = areaPickerParams.options;
    // const poolGroupOptions = useMemo(
    //  () =>
    //    (poolGroupList || []).map((v) => ({
    //      value: v?.id != null ? String(v.id) : String(v?.value ?? ''),
    //      label: v?.name ?? v?.pool_group_name ?? String(v?.id ?? v?.value ?? '')
    //    })),
    //  [poolGroupList]
    // );
    // ---- end lookups ----
    useImperativeHandle(ref, () => ({
      submitForm: () => formikRef.current?.submitForm?.()
    }));

    useEffect(() => {
      if (id || !open) return;

      const storedCompanyId = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      if (!storedCompanyId) return;

      if (companyOptions && companyOptions.length > 0) {
        const match = companyOptions.find((c) => String(c.value) === String(storedCompanyId));
        if (match) {
          setInitialValues((prev) => ({
            ...prev,
            company: String(match.value),
            company_name: match.label ?? match.name ?? String(match.value)
          }));
          setFilterCompanyId(String(match.value));
          return;
        }
      }
      // Set to localStorage value if not found in options yet (it might load later)
      setInitialValues((prev) => ({
        ...prev,
        company: String(storedCompanyId),
        company_name: String(storedCompanyId)
      }));
      setFilterCompanyId(String(storedCompanyId));
    }, [companyOptions, id, open]);

    useEffect(() => {
      let mounted = true;
      const fetch = async () => {
        if (!open) return;
        if (!id) {
          setLoading(false);

          if (passedWkt) {
            const geojsonFeature = wktToGeoJSON(passedWkt);
            if (geojsonFeature) {
              const fc = featureToFeatureCollection(geojsonFeature);
              const centroid = computeCentroidFromGeoJSON(geojsonFeature);
              const coordsStr = polygonCoordsStringFromGeoJSON(geojsonFeature);

              setInitialValues((prev) => ({
                ...prev,
                geom: passedWkt,
                info: fc,
                coords: coordsStr,
                centroid_lat: centroid.lat,
                centroid_lon: centroid.lon,
                latitude: centroid.lat,
                longitude: centroid.lon
              }));
            } else {
              setInitialValues((prev) => ({
                ...prev,
                geom: passedWkt
              }));
            }
          } else {
            // Reset initial values if no ID and no passed WKT, BUT preserve company
            setInitialValues((prev) => ({
              company: prev.company, // Preserve company
              company_name: prev.company_name, // Preserve company_name
              name: '',
              pool_code: '',
              country: null,
              country_name: '',
              pool_group: null,
              pool_group_name: '',
              area: null,
              area_name: '',
              address: '',
              remark: '',
              status: true,
              min_speed: '',
              max_speed: '',
              geom: null,
              geom_wkb: null,
              coords: null,
              centroid_lat: null,
              centroid_lon: null,
              info: null,
              latitude: null,
              longitude: null
            }));
          }

          return;
        }
        setLoading(true);
        try {
          const data = await getPoolById(id);
          if (!mounted) return;
          if (data) {
            const companyVal = data.company ?? data.company_id ?? null;
            setFilterCompanyId(companyVal ? String(companyVal) : '');

            const countryVal = data.country ?? data.country_id ?? null;
            const poolGroupVal = data.pool_group ?? data.pool_group_id ?? null;
            const areaVal = data.area ?? data.area_id ?? null;

            const cleanStr = (val) => (val === '-' ? '' : val);

            setInitialValues((prev) => ({
              ...prev,
              company: companyVal != null ? String(companyVal) : prev.company,
              company_name: data.company_name ?? data.join?.company_name ?? prev.company_name,
              name: cleanStr(data.name ?? data.pool_name) ?? prev.name,
              pool_code: cleanStr(data.pool_code) ?? prev.pool_code,
              country: countryVal != null ? String(countryVal) : prev.country,
              country_name: data.country_name ?? data.join?.country_name ?? prev.country_name,
              pool_group: poolGroupVal != null ? String(poolGroupVal) : prev.pool_group,
              pool_group_name: data.pool_group_name ?? data.join?.pool_group_name ?? prev.pool_group_name,
              area: areaVal != null ? String(areaVal) : prev.area,
              area_name: data.area_name ?? data.join?.area_name ?? prev.area_name,
              address: cleanStr(data.address) ?? prev.address,
              //  pic_name: cleanStr(data.pic_name) ?? prev.pic_name,
              //  pic_phone: cleanStr(data.pic_phone) ?? prev.pic_phone,
              status: typeof data.status === 'boolean' ? data.status : prev.status,
              min_speed: data.min_speed ?? prev.min_speed,
              max_speed: data.max_speed ?? prev.max_speed,
              geom: cleanStr(data.geom) ?? null,
              geom_wkb: cleanStr(data.geom_wkb) ?? null,
              coords: cleanStr(data.coords) ?? null,
              centroid_lat: cleanStr(data.centroid_lat) ?? null,
              centroid_lon: cleanStr(data.centroid_lon) ?? null,
              info: cleanStr(data.info) ?? null,
              latitude: cleanStr(data.latitude) ?? cleanStr(data.centroid_lat) ?? prev.latitude,
              longitude: cleanStr(data.longitude) ?? cleanStr(data.centroid_lon) ?? prev.longitude,
              remark: cleanStr(data.remark) ?? ''
            }));
          }
        } catch (err) {
          console.error('Failed to load pool:', err);
          notify({ message: 'Gagal memuat data pool', severity: 'error' }); // Use notify for error
          onError && onError('Gagal memuat data pool');
        } finally {
          if (mounted) setLoading(false);
        }
      };
      fetch();
      return () => {
        mounted = false;
      };
    }, [id, onError, open, passedWkt, notify]);

    const validationSchema = Yup.object().shape({
      company: Yup.mixed().required(t('geofenceList.validationCompanyRequired', 'Company is required')),
      name: Yup.string().required(t('validation.nameRequired', 'Name is required')),
      address: Yup.string().required(t('validation.addressRequired', 'Address is required'))
    });

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
      setSubmitting(true);
      onSubmitStart();
      try {
        if (values.company == null || values.company === '') throw new Error('Company id tidak valid');

        const numericOrNull = (v) => {
          if (v == null || v === '') return null;
          const n = Number(v);
          return Number.isNaN(n) ? v : n;
        };

        const payload = {
          id: id ?? 0,
          company: values.company != null ? Number(values.company) : null,
          name: formatUpperCase(values.name) ?? '',
          pool_code: values.pool_code || '-',
          country: numericOrNull(values.country),
          pool_group: numericOrNull(values.pool_group),
          area: numericOrNull(values.area),
          address: values.address || '-',
          status: Boolean(values.status),
          min_speed: values.min_speed ? Number(values.min_speed) : 0,
          max_speed: values.max_speed ? Number(values.max_speed) : 0,
          geom: values.geom || '-',
          geom_wkb: values.geom_wkb || '-',
          coords: values.coords || '-',
          centroid_lat: values.centroid_lat || '-',
          centroid_lon: values.centroid_lon || '-',
          info: values.info || '-',
          latitude: values.latitude || values.centroid_lat || '-',
          longitude: values.longitude || values.centroid_lon || '-',
          remark: values.remark || '-'
        };
        let response;
        if (id) {
          response = await updatePool(id, payload);
          notify({ message: t('notify.update', 'Updated successfully'), severity: 'success' });
        } else {
          response = await createPool(payload);
          notify({ message: t('notify.create', 'Created successfully'), severity: 'success' });
        }
        if (response?.code === 400 || response?.error === true || response?.error) {
          throw response;
        }
        resetForm();
        if (onSuccess) onSuccess();
        onClose && onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage =
          err?.response?.data?.message || err?.data?.message || err?.message || t('notify.failedSave', 'Failed to save data');
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
        onSubmitEnd();
      }
    };

    const handleViewMap = () => {
      setMapOpen(true);
    };

    return (
      <>
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {(formik) => {
            const { values, handleChange, handleBlur, setFieldValue, touched, errors, isSubmitting } = formik;
            return (
              <FormDialog
                open={open}
                onClose={onClose}
                title={id ? t('masterPit.edit', 'Edit Pit') : t('masterPit.add', 'Add Pit')}
                fullScreenMode={true}
                fullHeight={false}
                actions={
                  <>
                    <Button
                      variant="contained"
                      type="submit"
                      form="master-Pit-form"
                      disabled={isSubmitting || loading}
                      sx={{
                        backgroundColor: '#0072DA',
                        textTransform: 'none',
                        borderRadius: 1,
                        px: 3,
                        fontWeight: 700,
                        boxShadow: '0 8px 20px rgba(25,118,210,0.12)'
                      }}
                      startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                      {isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
                    </Button>
                    <Button onClick={onClose} disableRipple disableElevation variant="outlined">
                      {t('buttonTitle.cancel', 'Cancel')}
                    </Button>
                  </>
                }
              >
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, minHeight: 200, alignItems: 'center' }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box sx={{ width: '100%', maxWidth: 'none', px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
                    <form id="master-Pit-form" onSubmit={formik.handleSubmit}>
                      <Box sx={{ display: 'flex', gap: 4, alignItems: 'stretch', width: '100%' }}>
                        {/* KOLOM KIRI */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
                          <Stack spacing={2}>
                            <PickerField
                              label={`${t('masterPit.company', 'Company')} *`}
                              value={values.company}
                              {...companyPickerParams}
                              options={companyOptions}
                              allowAdd
                              AddComponent={MasterCompanyAddEdit}
                              onChange={(opt) => {
                                const val = typeof opt === 'object' ? opt?.value : opt;
                                const selected = companyOptions.find((c) => String(c.value) === String(val)) ?? null;
                                const idVal = selected ? selected.value : (val ?? null);
                                setFieldValue('company', idVal);
                                if (selected) setFieldValue('company_name', selected.label);
                                setFilterCompanyId(idVal || '');

                                // Reset fields dependent on company
                                setFieldValue('area', '');
                                setFieldValue('area_name', '');
                                setFieldValue('country', '');
                                setFieldValue('country_name', '');
                              }}
                              dialogTitle={t('general.selectCompany', 'Select Company')}
                              error={touched.company && Boolean(errors.company)}
                              helperText={touched.company && errors.company}
                            />

                            <TextField
                              label={t('masterPit.name', 'Pit Name')}
                              name="name"
                              value={values.name ?? ''}
                              onChange={(e) => {
                                setFieldValue('name', formatUpperCase(e.target.value));
                              }}
                              onBlur={handleBlur}
                              size="medium"
                              fullWidth
                              error={touched.name && Boolean(errors.name)}
                              helperText={touched.name && errors.name}
                              required
                              InputLabelProps={{
                                sx: {
                                  '& .MuiFormLabel-asterisk': {
                                    color: 'error.main'
                                  }
                                }
                              }}
                            />

                            <TextField
                              label={t('masterPit.code', 'Pit Code')}
                              name="pool_code"
                              value={values.pool_code ?? ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              size="medium"
                              fullWidth
                              required
                              InputLabelProps={{
                                sx: {
                                  '& .MuiFormLabel-asterisk': {
                                    color: 'error.main'
                                  }
                                }
                              }}
                            />

                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <TextField
                                label={t('masterPit.minSpeedInArea', 'Min Speed In Area')}
                                name="min_speed"
                                type="number"
                                value={values.min_speed ?? ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                size="medium"
                                fullWidth
                                error={touched.min_speed && Boolean(errors.min_speed)}
                                helperText={touched.min_speed && errors.min_speed}
                                InputProps={{
                                  endAdornment: <InputAdornment position="end">KM/H</InputAdornment>
                                }}
                              />
                              <TextField
                                label={t('masterPit.maxSpeedInArea', 'Max Speed In Area')}
                                name="max_speed"
                                type="number"
                                value={values.max_speed ?? ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                size="medium"
                                fullWidth
                                error={touched.max_speed && Boolean(errors.max_speed)}
                                helperText={touched.max_speed && errors.max_speed}
                                InputProps={{
                                  endAdornment: <InputAdornment position="end">KM/H</InputAdornment>
                                }}
                              />
                            </Box>

                            <PickerField
                              label={`${t('masterPit.country', 'Country')} *`}
                              value={values.country}
                              {...countryPickerParams}
                              options={countryOptions}
                              disabled={!filterCompanyId}
                              onChange={(opt) => {
                                const val = typeof opt === 'object' ? opt?.value : opt;
                                const selected = countryOptions.find((c) => String(c.value) === String(val)) ?? null;
                                const idVal = selected ? selected.value : (val ?? null);
                                setFieldValue('country', idVal);
                                if (selected) setFieldValue('country_name', selected.label);
                              }}
                              allowAdd
                              AddComponent={MasterCountryAddEdit}
                              dialogTitle={t('masterCountry.title', 'Master Country')}
                              error={touched.country && !!errors.country}
                              helperText={
                                !values.company
                                  ? t('general.selectCompanyFirst', '* Please select a company first')
                                  : touched.country && errors.country
                              }
                            />

                            {/* <PickerField
                            label="Pool Group"
                            value={values.pool_group}
                            options={poolGroupOptions}
                            onChange={(opt) => {
                              const selected = findOptionByValue(poolGroupOptions, opt) ?? (typeof opt === 'object' ? opt : null);
                              const idVal = selected ? getOptionId(selected) : (opt ?? null);
                              setFieldValue('pool_group', idVal);
                              if (selected) setFieldValue('pool_group_name', getOptionLabel(selected));
                            }}
                            dialogTitle="Select Pool Group"
                            getOptionId={(o) => getOptionId(o)}
                            getOptionLabel={(o) => getOptionLabel(o)}
                          /> */}

                            <PickerField
                              label={`${t('masterPit.area', 'Area')} *`}
                              value={values.area}
                              {...areaPickerParams}
                              options={areaOptions}
                              disabled={!filterCompanyId}
                              onChange={(opt) => {
                                const val = typeof opt === 'object' ? opt?.value : opt;
                                const selected = areaOptions.find((c) => String(c.value) === String(val)) ?? null;
                                const idVal = selected ? selected.value : (val ?? null);
                                setFieldValue('area', idVal);
                                if (selected) setFieldValue('area_name', selected.label);
                              }}
                              dialogTitle={t('menu.master-area', 'Master Area')}
                              allowAdd
                              AddComponent={MasterAreaAddEdit}
                              error={touched.area && !!errors.area}
                              helperText={
                                !values.company
                                  ? t('general.selectCompanyFirst', '* Please select a company first')
                                  : touched.area && errors.area
                              }
                            />
                          </Stack>
                        </Box>

                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
                          <Stack spacing={2}>
                            <TextField
                              label={t('masterPit.address', 'Address')}
                              name="address"
                              value={values.address ?? ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              size="medium"
                              fullWidth
                              error={touched.address && Boolean(errors.address)}
                              helperText={touched.address && errors.address}
                              required
                              InputLabelProps={{
                                sx: {
                                  '& .MuiFormLabel-asterisk': {
                                    color: 'error.main'
                                  }
                                }
                              }}
                            />
                            <TextField
                              label={t('masterPit.remark', 'Remark')}
                              name="remark"
                              value={values.remark ?? ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              size="medium"
                              fullWidth
                              InputLabelProps={{
                                sx: {
                                  '& .MuiFormLabel-asterisk': {
                                    color: 'error.main'
                                  }
                                }
                              }}
                            />

                            {/* <TextField
                            label="PIC Name"
                            name="pic_name"
                            value={values.pic_name ?? ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            size="medium"
                            fullWidth
                          />
                          <TextField
                            label="PIC Phone"
                            name="pic_phone"
                            value={values.pic_phone ?? ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            size="medium"
                            fullWidth
                          /> */}

                            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={Boolean(values.status)}
                                    onChange={(e) => setFieldValue('status', e.target.checked)}
                                    name="status"
                                  />
                                }
                                label={t('masterPit.status', 'Status')}
                              />
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <TextField
                                label={t('masterPit.drawLocation', 'Draw Location (WKT)')}
                                name="geom"
                                value={values.geom ?? ''}
                                onChange={(e) => {
                                  const formatted = e.target.value.toUpperCase().replace(/[^A-Z0-9\s().,-]/g, '');
                                  setFieldValue('geom', formatted);
                                }}
                                onBlur={handleBlur}
                                size="small"
                                fullWidth
                                multiline
                                minRows={2}
                                maxRows={5}
                                InputProps={{
                                  readOnly: !isEditingGeom,
                                  sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        onClick={() => setIsEditingGeom(!isEditingGeom)}
                                        edge="end"
                                        title={isEditingGeom ? 'Simpan' : 'Edit Manual'}
                                        sx={{
                                          bgcolor: isEditingGeom ? '#e8f5e9' : '#e3f2fd',
                                          color: isEditingGeom ? '#2e7d32' : '#1976d2',
                                          borderRadius: '10px',
                                          p: 0.8,
                                          boxShadow: isEditingGeom
                                            ? '0 2px 6px rgba(46, 125, 50, 0.2)'
                                            : '0 2px 6px rgba(25, 118, 210, 0.2)',
                                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                          '&:hover': {
                                            bgcolor: isEditingGeom ? '#c8e6c9' : '#bbdefb',
                                            transform: 'translateY(-2px)',
                                            boxShadow: isEditingGeom
                                              ? '0 4px 10px rgba(46, 125, 50, 0.3)'
                                              : '0 4px 10px rgba(25, 118, 210, 0.3)'
                                          }
                                        }}
                                      >
                                        {isEditingGeom ? <CheckIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                                      </IconButton>
                                    </InputAdornment>
                                  )
                                }}
                                helperText={
                                  values.info ? `Saved ${values.info.features?.length ?? 1} feature(s)` : 'There are no polygon yet'
                                }
                                required
                                InputLabelProps={{
                                  sx: {
                                    '& .MuiFormLabel-asterisk': {
                                      color: 'error.main'
                                    }
                                  }
                                }}
                              />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <Button
                                type="button"
                                variant="outlined"
                                startIcon={<MapIcon />}
                                onClick={handleViewMap}
                                disabled={disabledViewMap}
                              >
                                {t('masterPit.viewMap', 'View Map')}
                              </Button>
                              {disabledViewMap && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                  *To change the polygon, click cancel.
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </Box>
                      </Box>
                    </form>
                  </Box>
                )}
              </FormDialog>
            );
          }}
        </Formik>

        {mapOpen && (
          <DrawPolygon
            open={mapOpen}
            onClose={() => setMapOpen(false)}
            title={t('masterPit.drawLocation', 'Draw Polygon')}
            initialGeoJSON={formikRef.current?.values?.info}
            center={
              formikRef.current?.values?.centroid_lat && formikRef.current?.values?.centroid_lon
                ? [formikRef.current.values.centroid_lat, formikRef.current.values.centroid_lon]
                : undefined
            }
            zoom={formikRef.current?.values?.centroid_lat && formikRef.current?.values?.centroid_lon ? 16 : undefined}
            position="topleft"
            singleFeature={true}
            onSave={(geojson) => {
              if (!formikRef.current) return;
              const { setFieldValue } = formikRef.current;

              if (!geojson) {
                setFieldValue('info', null);
                setFieldValue('geom', null);
                setFieldValue('coords', null);
                setFieldValue('centroid_lat', null);
                setFieldValue('centroid_lon', null);
                setFieldValue('latitude', null);
                setFieldValue('longitude', null);
                return;
              }
              const fc = featureToFeatureCollection(geojson);
              const first = fc.features?.[0] ?? null;
              const wkt = geojsonToWKT(first ?? fc);
              const coordsStr = polygonCoordsStringFromGeoJSON(first ?? fc);
              const centroid = computeCentroidFromGeoJSON(first ?? fc);

              setFieldValue('info', fc);
              setFieldValue('geom', wkt);
              setFieldValue('coords', coordsStr);
              setFieldValue('centroid_lat', centroid.lat);
              setFieldValue('centroid_lon', centroid.lon);
              setFieldValue('latitude', centroid.lat);
              setFieldValue('longitude', centroid.lon);
            }}
          />
        )}
      </>
    );
  }
);

export default MasterPitAddEdit;
